import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useStaffMember, useStaffContacts, useStaffDeals, useStaffDealStats } from "@/hooks/use-staff";
import { useStaffOwnedContactDeals } from "@/hooks/use-staff-owned-contact-deals";
import { ContactDealsList } from "./ContactDealsList";
import { StatsTab } from "./StatsTab";
import { StaffInviteModal } from "./StaffInviteModal";
import { useState } from "react";
import { 
  Users, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  UserCircle, 
  Shield, 
  UserCog,
  Building,
  Handshake,
  TrendingUp,
  Camera,
  Image as ImageIcon,
  DollarSign,
  Edit,
  FileText,
  BarChart3,
  UserPlus,
  UserCheck
} from "lucide-react";

interface StaffDetailModalProps {
  staffId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: () => void;
}

export const StaffDetailModal = ({ staffId, open, onOpenChange, onEdit }: StaffDetailModalProps) => {
  const { data: staff, isLoading: staffLoading } = useStaffMember(staffId);
  const { data: contacts = [], isLoading: contactsLoading } = useStaffContacts(staffId);
  const { data: directDeals = [], isLoading: directDealsLoading } = useStaffDeals(staffId);
  const { data: ownedContactDeals = [], isLoading: ownedDealsLoading } = useStaffOwnedContactDeals(staffId);
  const { data: dealStats } = useStaffDealStats(staffId);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  // Combine direct deals and deals from owned contacts
  const allDeals = [...directDeals, ...ownedContactDeals];
  const dealsLoading = directDealsLoading || ownedDealsLoading;

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'superuser':
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'manager':
        return <UserCog className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superuser':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-orange-100 text-orange-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (staffLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-8">
            <div className="text-slate-600">Loading staff details...</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!staff) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-8">
            <div className="text-slate-600">Staff member not found</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="bg-slate-800 -mx-6 -mt-6 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-slate-50 flex items-center gap-2">
              <UserCircle className="h-5 w-5" />
              Staff Details
            </DialogTitle>
            <div className="flex gap-2">
              {!staff.user_id && (
                <Button
                  onClick={() => setInviteModalOpen(true)}
                  variant="outline"
                  size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Send Invitation
                </Button>
              )}
              {staff.user_id && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
                  <UserCheck className="h-4 w-4 mr-1" />
                  Account Active
                </Badge>
              )}
              {onEdit && (
                <Button
                  onClick={onEdit}
                  variant="outline"
                  size="sm"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="contacts">
              <Users className="h-4 w-4 mr-2" />
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="deals">
              <FileText className="h-4 w-4 mr-2" />
              Deals ({allDeals.length})
            </TabsTrigger>
            <TabsTrigger value="stats">
              <BarChart3 className="h-4 w-4 mr-2" />
              Stats
            </TabsTrigger>
            <TabsTrigger value="performance">
              <TrendingUp className="h-4 w-4 mr-2" />
              Performance
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="flex-1 overflow-auto">
            <div className="space-y-6 p-6">
              {/* Staff Header */}
              <div className="flex items-start gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage 
                    src={staff.profile_image_url} 
                    alt={`${staff.first_name} ${staff.last_name}`} 
                  />
                  <AvatarFallback className="bg-teal-100 text-teal-600 font-semibold text-lg">
                    {getInitials(staff.first_name, staff.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-slate-800">
                    {staff.first_name} {staff.last_name}
                  </h2>
                  {staff.title && (
                    <p className="text-lg text-slate-600 mt-1">{staff.title}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Badge className={`${getRoleBadgeColor(staff.permissions || staff.role)} flex items-center gap-1`}>
                      {getRoleIcon(staff.permissions || staff.role)}
                      {staff.permissions || staff.role}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Contact Information</h3>
                  
                  <div className="flex items-center gap-3 text-slate-600">
                    <Mail className="h-5 w-5" />
                    <span>{staff.email}</span>
                  </div>
                  
                  {staff.phone && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Phone className="h-5 w-5" />
                      <span>{staff.phone}</span>
                    </div>
                  )}
                  
                  {staff.address && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <MapPin className="h-5 w-5" />
                      <span>{staff.address}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Staff Information</h3>
                  
                  <div className="space-y-2">
                    <div className="text-sm text-slate-500">Role</div>
                    <div className="font-medium">{staff.permissions || staff.role}</div>
                  </div>
                  
                  {staff.title && (
                    <div className="space-y-2">
                      <div className="text-sm text-slate-500">Title</div>
                      <div className="font-medium">{staff.title}</div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <div className="text-sm text-slate-500">Account & Access Status</div>
                    <div className="flex items-center gap-2">
                      {staff.user_id ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            ✓ Active User Account
                          </Badge>
                          <div className="text-sm text-slate-600">Platform access enabled</div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            No Account
                          </Badge>
                          <div className="text-sm text-slate-600">Needs platform access invitation</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              {staff.details && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Additional Details</h3>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-slate-700 whitespace-pre-wrap">{staff.details}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Created</div>
                  <div className="text-sm font-medium">
                    {new Date(staff.created_at).toLocaleDateString()} at {new Date(staff.created_at).toLocaleTimeString()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Last Updated</div>
                  <div className="text-sm font-medium">
                    {new Date(staff.updated_at).toLocaleDateString()} at {new Date(staff.updated_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="contacts" className="flex-1 overflow-auto">
            <div className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">Contacts managed by {staff.first_name} {staff.last_name}</h3>
              {contactsLoading ? (
                <div className="text-center py-8">
                  <div className="text-slate-600">Loading contacts...</div>
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600">No contacts assigned to this staff member.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="p-4 border rounded-lg bg-white shadow-sm">
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={contact.profile_image} />
                          <AvatarFallback className="bg-slate-100 text-slate-600">
                            {contact.first_name?.charAt(0)}{contact.last_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800">
                            {contact.first_name} {contact.last_name}
                          </div>
                          <div className="text-sm text-slate-600 truncate">{contact.email}</div>
                          {contact.job_title && (
                            <div className="text-sm text-slate-500">{contact.job_title}</div>
                          )}
                          {contact.organizations && (
                            <div className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <Building className="h-3 w-3" />
                              {contact.organizations.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="deals" className="flex-1 overflow-auto">
            <ContactDealsList 
              deals={allDeals}
              isLoading={dealsLoading}
              title="Staff Deals"
              showContact={true}
              showAssignee={false}
            />
          </TabsContent>

          <TabsContent value="stats" className="flex-1 overflow-auto">
            <StatsTab 
              deals={allDeals}
              isLoading={dealsLoading}
              title={`${staff.first_name} ${staff.last_name} Statistics`}
            />
          </TabsContent>

          <TabsContent value="performance" className="flex-1 overflow-auto">
            <div className="space-y-6 p-6">
              <h3 className="text-lg font-semibold">Performance Overview</h3>
              
              {dealStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        Total Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{formatCurrency(dealStats.total.revenue)}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Handshake className="h-4 w-4 text-blue-600" />
                        Total Deals
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{allDeals.length}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Camera className="h-4 w-4 text-purple-600" />
                        Total Cameras
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{dealStats.total.cameras.toLocaleString()}</div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <ImageIcon className="h-4 w-4 text-orange-600" />
                        Total Images
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{dealStats.total.images.toLocaleString()}</div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Opportunities</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-lg font-semibold">{formatCurrency(dealStats?.opportunities.revenue || 0)}</div>
                    <div className="text-sm text-slate-600">
                      {dealStats?.opportunities.cameras || 0} cameras, {dealStats?.opportunities.images || 0} images
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Proposals</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-lg font-semibold">{formatCurrency(dealStats?.proposals.revenue || 0)}</div>
                    <div className="text-sm text-slate-600">
                      {dealStats?.proposals.cameras || 0} cameras, {dealStats?.proposals.images || 0} images
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Won Deals</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-lg font-semibold">{formatCurrency(dealStats?.deals.revenue || 0)}</div>
                    <div className="text-sm text-slate-600">
                      {dealStats?.deals.cameras || 0} cameras, {dealStats?.deals.images || 0} images
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Staff Invite Modal */}
        {staff && (
          <StaffInviteModal
            staffId={staffId}
            staffEmail={staff.email}
            staffName={`${staff.first_name} ${staff.last_name}`}
            open={inviteModalOpen}
            onOpenChange={setInviteModalOpen}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
